import React, { useEffect, useMemo, useRef, useState } from 'react'
import { floatingButtonStyle } from 'components/atoms/FloatingButton'
import { useFloatingSlot, FLOATING_PRIORITY } from 'components/atoms/floatingStack'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css, keyframes } from 'styled-components'
import SyncIcon from '@mui/icons-material/Sync'
import CloseIcon from '@mui/icons-material/Close'
import ReplayIcon from '@mui/icons-material/Replay'
import CircularProgress from '@mui/material/CircularProgress'
import { Dialog } from '@mui/material'
import {
  applyUnitReconciliationMatches as applyUnitReconciliationMatchesRequest,
  createUnitRevertPoint as createUnitRevertPointRequest,
  fetchUnitRevertPoint as fetchUnitRevertPointRequest,
  lookupUnitReconciliationPostcodes as lookupUnitReconciliationPostcodesRequest,
  previewUnitReconciliation as previewUnitReconciliationRequest,
  removeBpUnitsWithoutUprn as removeBpUnitsWithoutUprnRequest,
  revertUnitsToRevertPoint as revertUnitsToRevertPointRequest,
  updateUnitReconciliationIgnores as updateUnitReconciliationIgnoresRequest,
  updateUnitSyncPromptState as updateUnitSyncPromptStateRequest
} from 'services/unitsService'
import {
  fetchBpUnitsRoutine,
  syncUnitsRoutine,
  previewUnitSyncRoutine
} from 'ducks/units/actions'
import {
  selectBpUnits,
  selectUnitsFetched,
  selectUnitsLoading
} from 'ducks/units/selectors'
import UnitsSyncModal from 'features/createBp/components/atoms/UnitsSyncModal'
import UnitsReconciliationModal from 'features/bpDetails/Components/units/UnitsReconciliationModal'
import {
  normalizeUnitReconciliationPreview,
  normalizeUnitSyncPreview,
  notifyFloorPrefill
} from 'utils/unitSync'
import { getApiErrors } from 'utils/errors'
import { toast } from 'react-toastify'
import { fetchBpRoutine } from 'features/bpDetails/ducks/actions'

const buildPreviewCacheKey = ({
  bp,
  expandNearby,
  nearbyRadiusIndex,
  existingUnitsSignature
}) =>
  [
    bp?.id || '',
    bp?.uprn || '',
    bp?.postalCode || bp?.postal_code || '',
    expandNearby ? 'nearby' : 'postcode',
    expandNearby ? (nearbyRadiusIndex ?? 'none') : 'base',
    existingUnitsSignature || 'no-units'
  ].join('|')

const buildReconciliationCacheKey = ({
  bp,
  expandNearby,
  nearbyRadiusIndex,
  existingUnitsSignature,
  selectedUprnsSignature
}) =>
  [
    'reconciliation',
    bp?.id || '',
    bp?.uprn || '',
    bp?.postalCode || bp?.postal_code || '',
    expandNearby ? 'nearby' : 'postcode',
    expandNearby ? (nearbyRadiusIndex ?? 'none') : 'base',
    existingUnitsSignature || 'no-units',
    selectedUprnsSignature || 'all'
  ].join('|')

const defaultPromptState = {
  autoOpenedAt: null,
  checkedAt: null,
  dismissedAt: null,
  completedAt: null,
  isCurrent: false,
  latestRelevantChangeAt: null
}

const normalizePromptState = promptState => ({
  autoOpenedAt:
    promptState?.autoOpenedAt || promptState?.auto_opened_at || null,
  checkedAt: promptState?.checkedAt || promptState?.checked_at || null,
  dismissedAt: promptState?.dismissedAt || promptState?.dismissed_at || null,
  completedAt: promptState?.completedAt || promptState?.completed_at || null,
  isCurrent: promptState?.isCurrent ?? promptState?.is_current ?? false,
  latestRelevantChangeAt:
    promptState?.latestRelevantChangeAt ||
    promptState?.latest_relevant_change_at ||
    null
})

const getPreviewActionableCounts = previewSnapshot => {
  const matched = Array.isArray(previewSnapshot?.matched)
    ? previewSnapshot.matched.filter(item => !item.already_exists).length
    : 0
  const unmatched = Array.isArray(previewSnapshot?.unmatched)
    ? previewSnapshot.unmatched.length
    : 0

  return {
    actionableMatchedCount: matched,
    actionableUnmatchedCount: unmatched,
    actionableTotalCount: matched + unmatched
  }
}

const hasActionablePreviewResults = previewSnapshot =>
  getPreviewActionableCounts(previewSnapshot).actionableTotalCount > 0

const getReconciliationActionableCounts = reconciliationSnapshot => {
  const summary = reconciliationSnapshot?.summary || {}
  const apiUnitsCount = Number(
    summary?.actionable_api_units_count ??
      (Array.isArray(reconciliationSnapshot?.api_units)
        ? reconciliationSnapshot.api_units.filter(item => !item?.is_ignored)
          .length
        : 0)
  )

  return {
    apiUnitsCount,
    existingUnitsCount: Number(
      summary?.existing_units_count ||
        (Array.isArray(reconciliationSnapshot?.existing_units)
          ? reconciliationSnapshot.existing_units.length
          : 0)
    ),
    reviewCount: Array.isArray(reconciliationSnapshot?.api_units)
      ? reconciliationSnapshot.api_units.filter(
        item => !item?.is_ignored && item?.requires_review
      ).length
      : 0,
    exactMatchCount: Number(summary?.suggested_exact_uprn_matches_count || 0)
  }
}

const isWholeBuildingPlaceholderUnit = unit => {
  const unitName = String(unit?.name || '')
    .trim()
    .toLowerCase()

  return unitName.startsWith('entirety of ')
}

const normalizeUprn = value => String(value || '').trim()

const normalizeSelectedUprns = values =>
  Array.from(
    new Set(
      (values || [])
        .map(value => normalizeUprn(value))
        .filter(Boolean)
    )
  )

const buildSelectedUprnsSignature = selectedUprns =>
  normalizeSelectedUprns(selectedUprns).join(',')

const isReferenceBuildingUnit = (unit, bp) => {
  const unitUprn = normalizeUprn(unit?.uprn)
  const bpUprn = normalizeUprn(bp?.uprn)

  return Boolean(unitUprn && bpUprn && unitUprn === bpUprn)
}

const AddressUnitsSyncPrompt = ({
  bp,
  enabled,
  manualOpenKey = 0,
  manualOpenPayload = null,
  suppressReminderWidget = false
}) => {
  const dispatch = useDispatch()
  const bpUnits = useSelector(selectBpUnits)
  const unitsLoading = useSelector(selectUnitsLoading)
  const unitsFetched = useSelector(selectUnitsFetched)
  const [open, setOpen] = useState(false)
  const [reconciliationOpen, setReconciliationOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [postcodeLookupLoading, setPostcodeLookupLoading] = useState(false)
  const [previewData, setPreviewData] = useState(null)
  const [lastPreviewData, setLastPreviewData] = useState(null)
  const [reconciliationData, setReconciliationData] = useState(null)
  const [lastReconciliationData, setLastReconciliationData] = useState(null)
  const [previewExpandedNearby, setPreviewExpandedNearby] = useState(false)
  const [nearbyRadiusIndex, setNearbyRadiusIndex] = useState(null)
  const [reminderResolved, setReminderResolved] = useState(false)
  const [revertPoint, setRevertPoint] = useState(null)
  const [reverting, setReverting] = useState(false)
  const [revertConfirmOpen, setRevertConfirmOpen] = useState(false)
  const [promptState, setPromptState] = useState(() =>
    normalizePromptState(bp?.unitSyncPromptState)
  )
  const [promptStateHydrated, setPromptStateHydrated] = useState(false)
  const previewRequestInFlightRef = useRef(false)
  const openPreviewWhenReadyRef = useRef(false)
  const reconciliationRequestInFlightRef = useRef(false)
  const openReconciliationWhenReadyRef = useRef(false)
  const dismissedRef = useRef(false)
  const handledManualOpenKeyRef = useRef(0)
  const previewCacheRef = useRef(new Map())
  const reconciliationCacheRef = useRef(new Map())
  const existingUnitsCountRef = useRef(0)
  const previewConfirmContextRef = useRef({
    expandNearby: false,
    nearbyRadiusIndex: null
  })

  const persistPromptState = (patch, event = null) => {
    setPromptState(previousState => ({
      ...previousState,
      ...patch
    }))

    if (!bp?.id || !event) {
      return
    }

    updateUnitSyncPromptStateRequest({
      bpId: bp.id,
      event
    }).catch(() => {})
  }

  const markPromptStateAsCheckedLocally = ({ completed = false } = {}) => {
    const now = new Date().toISOString()

    setPromptState(previousState => ({
      ...previousState,
      checkedAt: now,
      dismissedAt: null,
      isCurrent: true,
      ...(completed ? { completedAt: now } : {})
    }))

    if (bp?.id) {
      updateUnitSyncPromptStateRequest({
        bpId: bp.id,
        event: completed ? 'completed' : 'checked',
      }).catch(() => {})
    }
  }

  const checkedAtTimestamp = promptState.checkedAt
    ? Date.parse(promptState.checkedAt)
    : NaN
  const completedAtTimestamp = promptState.completedAt
    ? Date.parse(promptState.completedAt)
    : NaN
  const latestRelevantChangeTimestamp = promptState.latestRelevantChangeAt
    ? Date.parse(promptState.latestRelevantChangeAt)
    : NaN
  const hasCurrentCheckedState =
    Boolean(promptState.isCurrent) && Boolean(promptState.checkedAt)
  const hasCurrentCompletedState =
    hasCurrentCheckedState &&
    Boolean(promptState.completedAt) &&
    !Number.isNaN(completedAtTimestamp) &&
    (Number.isNaN(latestRelevantChangeTimestamp)
      ? !Number.isNaN(checkedAtTimestamp) &&
        completedAtTimestamp >= checkedAtTimestamp
      : completedAtTimestamp >= latestRelevantChangeTimestamp)

  const isActiveBp = String(bp?.status || '') === 'active'
  const canSyncUnits =
    enabled &&
    isActiveBp &&
    Boolean(bp?.id) &&
    Boolean(bp?.uprn || bp?.postalCode)
  const existingReconciliationUnits = useMemo(
    () =>
      (bpUnits || []).filter(
        unit =>
          unit?.uprn &&
          !isWholeBuildingPlaceholderUnit(unit) &&
          !isReferenceBuildingUnit(unit, bp)
      ),
    [bpUnits, bp]
  )
  const existingUnitsSignature = useMemo(
    () =>
      existingReconciliationUnits
        .map(unit => unit?.uprn || unit?.id || '')
        .filter(Boolean)
        .map(String)
        .sort()
        .join('|'),
    [existingReconciliationUnits]
  )
  const existingReconciliationUnitsCount = existingReconciliationUnits.length
  const shouldUseReconciliationFlow = existingReconciliationUnitsCount > 0
  const shouldUseOsMapFirst = existingReconciliationUnitsCount === 0
  const hasUnitsWithoutUprn = useMemo(
    () =>
      (bpUnits || []).some(
        unit =>
          !isWholeBuildingPlaceholderUnit(unit) &&
          !isReferenceBuildingUnit(unit, bp) &&
          !normalizeUprn(unit?.uprn)
      ),
    [bpUnits, bp]
  )

  useEffect(() => {
    existingUnitsCountRef.current = existingReconciliationUnitsCount
  }, [existingReconciliationUnitsCount])

  useEffect(() => {
    if (!bp?.id) {
      setPromptState(defaultPromptState)
      setPromptStateHydrated(false)
      return
    }

    setPromptState(normalizePromptState(bp?.unitSyncPromptState))
    setPromptStateHydrated(true)
  }, [
    bp?.id,
    bp?.unitSyncPromptState?.autoOpenedAt,
    bp?.unitSyncPromptState?.auto_opened_at,
    bp?.unitSyncPromptState?.checkedAt,
    bp?.unitSyncPromptState?.checked_at,
    bp?.unitSyncPromptState?.dismissedAt,
    bp?.unitSyncPromptState?.dismissed_at,
    bp?.unitSyncPromptState?.completedAt,
    bp?.unitSyncPromptState?.completed_at,
    bp?.unitSyncPromptState?.latestRelevantChangeAt,
    bp?.unitSyncPromptState?.latest_relevant_change_at,
    bp?.unitSyncPromptState?.isCurrent,
    bp?.unitSyncPromptState?.is_current
  ])

  useEffect(() => {
    previewRequestInFlightRef.current = false
    openPreviewWhenReadyRef.current = false
    reconciliationRequestInFlightRef.current = false
    openReconciliationWhenReadyRef.current = false
    dismissedRef.current = false
    previewCacheRef.current.clear()
    reconciliationCacheRef.current.clear()
    existingUnitsCountRef.current = 0
    setOpen(false)
    setReconciliationOpen(false)
    setLoading(false)
    setConfirming(false)
    setPostcodeLookupLoading(false)
    setPreviewData(null)
    setLastPreviewData(null)
    setReconciliationData(null)
    setLastReconciliationData(null)
    setPreviewExpandedNearby(false)
    setNearbyRadiusIndex(null)
    setReminderResolved(false)
  }, [bp?.id])

  useEffect(() => {
    const previewSnapshot = previewData || lastPreviewData
    const previewSnapshotExpandedNearby = previewSnapshot?.nearby_expanded
    const previewSnapshotRadiusIndex = previewSnapshot?.nearby_radius_index

    previewConfirmContextRef.current = {
      expandNearby:
        previewSnapshotExpandedNearby !== undefined
          ? Boolean(previewSnapshotExpandedNearby)
          : Boolean(previewExpandedNearby),
      nearbyRadiusIndex: Number.isInteger(previewSnapshotRadiusIndex)
        ? previewSnapshotRadiusIndex
        : Number.isInteger(nearbyRadiusIndex)
          ? nearbyRadiusIndex
          : null
    }
  }, [previewData, lastPreviewData, previewExpandedNearby, nearbyRadiusIndex])

  const reminderPreviewSnapshot = shouldUseReconciliationFlow
    ? reconciliationData || lastReconciliationData
    : previewData || lastPreviewData
  const isPreviewLoadingInBackground = loading && !open && !reconciliationOpen
  const reminderCounts = shouldUseReconciliationFlow
    ? getReconciliationActionableCounts(reminderPreviewSnapshot)
    : getPreviewActionableCounts(reminderPreviewSnapshot)
  const reminderActionableTotalCount = shouldUseReconciliationFlow
    ? reminderCounts.apiUnitsCount
    : reminderCounts.actionableTotalCount
  const isReminderUrgent = !hasCurrentCheckedState
  const showReminderWidget =
    promptStateHydrated &&
    canSyncUnits &&
    !suppressReminderWidget &&
    (unitsFetched || !unitsLoading) &&
    !shouldUseOsMapFirst &&
    hasUnitsWithoutUprn &&
    !reminderResolved &&
    !hasCurrentCheckedState &&
    !open &&
    !reconciliationOpen &&
    !confirming

  const reminderSlot = useFloatingSlot('units-reminder', FLOATING_PRIORITY.reminder, showReminderWidget)

  const showRevertWidget =
    Boolean(revertPoint) &&
    canSyncUnits &&
    !open &&
    !reconciliationOpen &&
    !confirming

  const revertSlot = useFloatingSlot('units-revert', FLOATING_PRIORITY.info, showRevertWidget)

  useEffect(() => {
    let cancelled = false

    if (!bp?.id || !enabled) {
      setRevertPoint(null)
      return undefined
    }

    fetchUnitRevertPointRequest({ bpId: bp.id })
      .then(({ data }) => {
        if (!cancelled) setRevertPoint(data?.data || null)
      })
      .catch(() => {})

    return () => {
      cancelled = true
    }
  }, [bp?.id, enabled])

  const loadPreview = ({
    openOnLoad = true,
    openWhenResolved = true,
    expandNearby = false,
    nearbyRadiusIndexOverride = null
  } = {}) => {
    if (!canSyncUnits) {
      return
    }

    if (previewRequestInFlightRef.current) {
      if (openOnLoad || openWhenResolved) {
        openPreviewWhenReadyRef.current = true
      }
      return
    }

    const requestNearbyRadiusIndex = expandNearby
      ? nearbyRadiusIndexOverride
      : null

    if (expandNearby && requestNearbyRadiusIndex == null) {
      return
    }

    const previewCacheKey = buildPreviewCacheKey({
      bp,
      expandNearby,
      nearbyRadiusIndex: requestNearbyRadiusIndex,
      existingUnitsSignature
    })
    const cachedPreview = previewCacheRef.current.get(previewCacheKey)

    if (cachedPreview) {
      dismissedRef.current = false
      openPreviewWhenReadyRef.current = false
      setOpen(openOnLoad || openWhenResolved)
      setLoading(false)
      setLastPreviewData(cachedPreview)
      if (openOnLoad || openWhenResolved) {
        setPreviewData(cachedPreview)
      }
      setPreviewExpandedNearby(expandNearby)
      setNearbyRadiusIndex(expandNearby ? requestNearbyRadiusIndex : null)
      return
    }

    dismissedRef.current = false
    previewRequestInFlightRef.current = true
    if (openOnLoad || openWhenResolved) {
      openPreviewWhenReadyRef.current = true
    }
    setLoading(true)
    setOpen(openOnLoad)
    if (openOnLoad) {
      setPreviewData(null)
    }
    setPreviewExpandedNearby(expandNearby)

    if (!expandNearby) {
      setNearbyRadiusIndex(null)
    }

    dispatch(
      previewUnitSyncRoutine({
        bpId: bp.id,
        expandNearby,
        nearbyRadiusIndex: requestNearbyRadiusIndex,
        callback: data => {
          const shouldOpenPreview =
            openOnLoad || openWhenResolved || openPreviewWhenReadyRef.current

          previewRequestInFlightRef.current = false
          openPreviewWhenReadyRef.current = false
          setLoading(false)

          if (dismissedRef.current) {
            return
          }

          const hasExistingUnits = existingUnitsCountRef.current > 0

          if (!data) {
            if (hasExistingUnits) {
              setReminderResolved(true)
              persistPromptState({ dismissedAt: null }, 'clear_dismissed')
              setOpen(false)
              setPreviewData(null)
              if (shouldOpenPreview) {
                toast.info(
                  'Unit preview is not available right now. Existing units are already attached to this Building Passport.'
                )
              }
            } else {
              setOpen(shouldOpenPreview)
            }
            return
          }

          const normalizedPreviewData = normalizeUnitSyncPreview(data)
          previewCacheRef.current.set(previewCacheKey, normalizedPreviewData)

          if (expandNearby) {
            setNearbyRadiusIndex(requestNearbyRadiusIndex)
          } else {
            setNearbyRadiusIndex(null)
          }

          const matchedCount = (normalizedPreviewData?.matched || []).length
          const unmatchedCount = (normalizedPreviewData?.unmatched || []).length
          const hasPreviewResults = matchedCount > 0 || unmatchedCount > 0
          const actionableMatchedCount = (
            normalizedPreviewData?.matched || []
          ).filter(item => !item.already_exists).length
          const actionableUnmatchedCount = (
            normalizedPreviewData?.unmatched || []
          ).length
          const hasActionableResultsInPreview =
            actionableMatchedCount > 0 || actionableUnmatchedCount > 0

          if (!hasPreviewResults && hasExistingUnits) {
            setReminderResolved(true)
            persistPromptState({ dismissedAt: null }, 'clear_dismissed')
            setOpen(false)
            setPreviewData(null)
            shouldOpenPreview &&
              toast.info(
                'No new matching addresses were found. Existing units are already attached to this Building Passport.'
              )
            return
          }

          if (!hasActionableResultsInPreview && hasExistingUnits) {
            setReminderResolved(true)
            persistPromptState({ dismissedAt: null }, 'clear_dismissed')
            setOpen(false)
            setPreviewData(null)
            shouldOpenPreview &&
              toast.info(
                'All matching units are already attached to this Building Passport.'
              )
            return
          }

          markPromptStateAsCheckedLocally()
          setLastPreviewData(normalizedPreviewData)
          setPreviewData(shouldOpenPreview ? normalizedPreviewData : null)
          setOpen(shouldOpenPreview)
        }
      })
    )
  }

  const loadReconciliationPreview = ({
    openOnLoad = true,
    openWhenResolved = true,
    expandNearby = false,
    nearbyRadiusIndexOverride = null,
    selectedUprns = []
  } = {}) => {
    if (!canSyncUnits) {
      return
    }

    if (reconciliationRequestInFlightRef.current) {
      if (openOnLoad || openWhenResolved) {
        openReconciliationWhenReadyRef.current = true
      }
      return
    }

    const requestNearbyRadiusIndex = expandNearby
      ? nearbyRadiusIndexOverride
      : null
    const normalizedSelectedUprns = normalizeSelectedUprns(selectedUprns)
    const selectedUprnsSignature = buildSelectedUprnsSignature(
      normalizedSelectedUprns
    )

    if (expandNearby && requestNearbyRadiusIndex == null) {
      return
    }

    const reconciliationCacheKey = buildReconciliationCacheKey({
      bp,
      expandNearby,
      nearbyRadiusIndex: requestNearbyRadiusIndex,
      existingUnitsSignature,
      selectedUprnsSignature
    })
    const cachedPreview = reconciliationCacheRef.current.get(
      reconciliationCacheKey
    )

    if (cachedPreview) {
      dismissedRef.current = false
      openReconciliationWhenReadyRef.current = false
      setReconciliationOpen(openOnLoad || openWhenResolved)
      setOpen(false)
      setLoading(false)
      setLastReconciliationData(cachedPreview)
      if (openOnLoad || openWhenResolved) {
        setReconciliationData(cachedPreview)
      }
      setPreviewExpandedNearby(Boolean(cachedPreview?.preview?.nearby_expanded))
      setNearbyRadiusIndex(
        Number.isInteger(cachedPreview?.preview?.nearby_radius_index)
          ? cachedPreview.preview.nearby_radius_index
          : null
      )
      return
    }

    dismissedRef.current = false
    reconciliationRequestInFlightRef.current = true
    if (openOnLoad || openWhenResolved) {
      openReconciliationWhenReadyRef.current = true
    }
    setLoading(true)
    setOpen(false)
    setReconciliationOpen(openOnLoad)
    if (openOnLoad && !lastReconciliationData) {
      setReconciliationData(null)
    }

    previewUnitReconciliationRequest({
      bpId: bp.id,
      expandNearby,
      nearbyRadiusIndex: requestNearbyRadiusIndex,
      selectedUprns: normalizedSelectedUprns
    })
      .then(({ data }) => {
        const shouldOpenPreview =
          openOnLoad ||
          openWhenResolved ||
          openReconciliationWhenReadyRef.current

        reconciliationRequestInFlightRef.current = false
        openReconciliationWhenReadyRef.current = false
        setLoading(false)

        if (dismissedRef.current) {
          return
        }

        const normalizedData = normalizeUnitReconciliationPreview(data?.data)

        if (!normalizedData) {
          setReminderResolved(true)
          persistPromptState({ dismissedAt: null }, 'clear_dismissed')
          setReconciliationOpen(false)
          shouldOpenPreview &&
            toast.info(
              'Reconciliation preview is not available right now for this Building Passport.'
            )
          return
        }

        reconciliationCacheRef.current.set(
          reconciliationCacheKey,
          normalizedData
        )

        const apiUnitsCount = Number(
          normalizedData?.summary?.api_units_count ||
            normalizedData?.api_units?.length ||
            0
        )

        if (apiUnitsCount <= 0) {
          setReminderResolved(true)
          persistPromptState({ dismissedAt: null }, 'clear_dismissed')
          setReconciliationOpen(false)
          setReconciliationData(null)
          shouldOpenPreview &&
            toast.info(
              'No address units were found to compare with the units already saved in this Building Passport.'
            )
          return
        }

        markPromptStateAsCheckedLocally()
        setPreviewExpandedNearby(
          Boolean(normalizedData?.preview?.nearby_expanded)
        )
        setNearbyRadiusIndex(
          Number.isInteger(normalizedData?.preview?.nearby_radius_index)
            ? normalizedData.preview.nearby_radius_index
            : null
        )
        setLastReconciliationData(normalizedData)
        setReconciliationData(shouldOpenPreview ? normalizedData : null)
        setReconciliationOpen(shouldOpenPreview)
      })
      .catch(error => {
        reconciliationRequestInFlightRef.current = false
        openReconciliationWhenReadyRef.current = false
        setLoading(false)
        setReconciliationOpen(false)
        setReconciliationData(null)
        toast.error(
          getApiErrors(error) ||
            'Could not load the unit comparison preview. Please try again.'
        )
      })
  }

  const handleOpenReviewFlow = options => {
    const selectedUprns = normalizeSelectedUprns(options?.selectedUprns)

    if (shouldUseReconciliationFlow || selectedUprns.length) {
      loadReconciliationPreview(options)
      return
    }

    loadPreview(options)
  }

  useEffect(() => {
    if (
      !manualOpenKey ||
      handledManualOpenKeyRef.current === manualOpenKey ||
      !canSyncUnits ||
      (unitsLoading && !unitsFetched)
    ) {
      return
    }

    handledManualOpenKeyRef.current = manualOpenKey

    const selectedUprns = normalizeSelectedUprns(
      manualOpenPayload?.selectedUprns
    )

    if (shouldUseOsMapFirst && !selectedUprns.length) {
      toast.info('Select the building outline on the OS map before syncing units.')
      return
    }

    handleOpenReviewFlow({
      openOnLoad: true,
      openWhenResolved: true,
      selectedUprns
    })
  }, [
    manualOpenKey,
    manualOpenPayload,
    canSyncUnits,
    shouldUseOsMapFirst,
    unitsLoading,
    unitsFetched
  ])

  const handleSkip = ({
    persistReminder,
    syncStateEvent = 'clear_dismissed'
  } = {}) => {
    const previewSnapshot = previewData || lastPreviewData
    const shouldPersistReminder =
      persistReminder !== undefined
        ? persistReminder
        : loading || hasActionablePreviewResults(previewSnapshot)

    dismissedRef.current = true
    if (shouldPersistReminder) {
      persistPromptState({ dismissedAt: new Date().toISOString() }, 'dismissed')
    } else {
      persistPromptState({ dismissedAt: null }, syncStateEvent)
    }
    setOpen(false)
    setPreviewData(null)
    setPreviewExpandedNearby(false)
    setNearbyRadiusIndex(null)
  }

  const handleSkipReconciliation = ({
    persistReminder,
    syncStateEvent = 'clear_dismissed'
  } = {}) => {
    const reconciliationSnapshot = reconciliationData || lastReconciliationData
    const shouldPersistReminder =
      persistReminder !== undefined
        ? persistReminder
        : loading ||
          getReconciliationActionableCounts(reconciliationSnapshot)
            .apiUnitsCount > 0

    dismissedRef.current = true
    if (shouldPersistReminder) {
      persistPromptState({ dismissedAt: new Date().toISOString() }, 'dismissed')
    } else {
      persistPromptState({ dismissedAt: null }, syncStateEvent)
    }

    setReconciliationOpen(false)
    setReconciliationData(null)
    setPreviewExpandedNearby(false)
    setNearbyRadiusIndex(null)
  }

  const handleOpenClassicSyncFromReconciliation = () => {
    const rawPreviewData =
      reconciliationData?.preview || lastReconciliationData?.preview

    if (!rawPreviewData) {
      toast.info(
        'The current sync preview is not available yet for this comparison.'
      )
      return
    }

    const normalizedPreviewData = normalizeUnitSyncPreview(rawPreviewData)

    setReconciliationOpen(false)
    setReconciliationData(null)
    setLastPreviewData(normalizedPreviewData)
    setPreviewData(normalizedPreviewData)
    setPreviewExpandedNearby(Boolean(normalizedPreviewData?.nearby_expanded))
    setNearbyRadiusIndex(
      Number.isInteger(normalizedPreviewData?.nearby_radius_index)
        ? normalizedPreviewData.nearby_radius_index
        : null
    )
    setOpen(true)
  }

  const handleConfirmUnits = selection => {
    const selectedUprns = Array.isArray(selection)
      ? selection
      : selection?.uprns || []

    if (!selectedUprns.length) {
      handleSkip()
      return
    }

    const selectionPreviewContext = !Array.isArray(selection)
      ? selection?.previewContext
      : null
    const requestExpandNearby =
      selectionPreviewContext?.nearbyExpanded !== undefined
        ? Boolean(selectionPreviewContext.nearbyExpanded)
        : previewConfirmContextRef.current.expandNearby
    const requestNearbyRadiusIndex = Number.isInteger(
      selectionPreviewContext?.nearbyRadiusIndex
    )
      ? selectionPreviewContext.nearbyRadiusIndex
      : previewConfirmContextRef.current.nearbyRadiusIndex

    setConfirming(true)
    dispatch(
      syncUnitsRoutine({
        bpId: bp.id,
        selectedUprns,
        expandNearby: requestExpandNearby,
        nearbyRadiusIndex: requestNearbyRadiusIndex,
        isWizard: false,
        callback: result => {
          setConfirming(false)

          if (result?.ok) {
            markPromptStateAsCheckedLocally({ completed: true })
            previewCacheRef.current.clear()
            reconciliationCacheRef.current.clear()
            handleSkip({ persistReminder: false, syncStateEvent: null })
          }
        }
      })
    )
  }

  const handleApplyReconciliationMatches = async matches => {
    if (!bp?.id || !Array.isArray(matches) || matches.length === 0) {
      return
    }

    let data

    try {
      const response = await applyUnitReconciliationMatchesRequest({
        bpId: bp.id,
        matches
      })
      data = response.data
    } catch (error) {
      toast.error(
        getApiErrors(error) || 'Could not update the selected unit matches'
      )
      throw error
    }

    const updatedCount = Number(
      data?.meta?.updatedCount || data?.meta?.updated_count || matches.length
    )

    toast.success(
      `${updatedCount} ${updatedCount === 1 ? 'unit has' : 'units have'} been updated`
    )
    notifyFloorPrefill(data?.meta)
    // Pre-fill may have set the building's floors; reload the BP so the
    // unit form offers them.
    dispatch(fetchBpRoutine({ id: bp.id }))

    markPromptStateAsCheckedLocally({ completed: true })
    previewCacheRef.current.clear()
    reconciliationCacheRef.current.clear()
    setReconciliationData(null)
    setLastReconciliationData(null)
    setReconciliationOpen(false)
    setReminderResolved(true)
    dispatch(fetchBpUnitsRoutine({ bpId: bp.id }))
  }

  const handleConfirmReconciliationReview = async ({
    matches = [],
    selectedUprns = [],
    previewContext = null
  } = {}) => {
    if (!bp?.id) {
      return
    }

    const selectedMatches = Array.isArray(matches) ? matches : []
    const selectedCreateUprns = Array.isArray(selectedUprns)
      ? selectedUprns.filter(Boolean)
      : []

    if (!selectedMatches.length && !selectedCreateUprns.length) {
      return
    }

    let updatedCount = 0

    if (selectedMatches.length) {
      try {
        const response = await applyUnitReconciliationMatchesRequest({
          bpId: bp.id,
          matches: selectedMatches
        })
        updatedCount = Number(
          response?.data?.meta?.updatedCount ||
            response?.data?.meta?.updated_count ||
            selectedMatches.length
        )
        notifyFloorPrefill(response?.data?.meta)
        dispatch(fetchBpRoutine({ id: bp.id }))
      } catch (error) {
        toast.error(
          getApiErrors(error) || 'Could not update the selected unit matches'
        )
        throw error
      }
    }

    if (selectedCreateUprns.length) {
      const syncResult = await new Promise(resolve => {
        dispatch(
          syncUnitsRoutine({
            bpId: bp.id,
            selectedUprns: selectedCreateUprns,
            expandNearby:
              previewContext?.nearbyExpanded !== undefined
                ? Boolean(previewContext.nearbyExpanded)
                : previewConfirmContextRef.current.expandNearby,
            nearbyRadiusIndex: Number.isInteger(
              previewContext?.nearbyRadiusIndex
            )
              ? previewContext.nearbyRadiusIndex
              : previewConfirmContextRef.current.nearbyRadiusIndex,
            isWizard: false,
            callback: resolve
          })
        )
      })

      if (!syncResult?.ok) {
        throw new Error('Could not create the selected units')
      }
    }

    const summaryParts = []
    if (updatedCount > 0) {
      summaryParts.push(
        `${updatedCount} ${updatedCount === 1 ? 'unit updated' : 'units updated'}`
      )
    }
    if (summaryParts.length) {
      toast.success(summaryParts.join(', '))
    }

    markPromptStateAsCheckedLocally({ completed: true })
    previewCacheRef.current.clear()
    reconciliationCacheRef.current.clear()
    setReconciliationData(null)
    setLastReconciliationData(null)
    setReconciliationOpen(false)
    setReminderResolved(true)
    dispatch(fetchBpUnitsRoutine({ bpId: bp.id }))
  }

  // "Use public data": adopt the official address list wholesale. Matches
  // run first so those saved units gain a UPRN and survive the final
  // delete-without-UPRN pass; unit creation runs second so the passport is
  // never left empty by that pass.
  const handleUsePublicData = async ({
    matches = [],
    selectedUprns = [],
    previewContext = null
  } = {}) => {
    if (!bp?.id) {
      return
    }

    const selectedMatches = Array.isArray(matches) ? matches : []
    const selectedCreateUprns = Array.isArray(selectedUprns)
      ? selectedUprns.filter(Boolean)
      : []

    // Snapshot the current unit list first so the whole operation can be
    // undone from the "Revert unit changes" widget.
    let createdRevertPoint = null
    try {
      const { data } = await createUnitRevertPointRequest({
        bpId: bp.id,
        label: 'use-public-data'
      })
      createdRevertPoint = data?.data || null
    } catch (error) {
      toast.error(
        getApiErrors(error) ||
          'Could not save a revert point — no unit changes were made.'
      )
      throw error
    }

    let updatedCount = 0

    if (selectedMatches.length) {
      try {
        const response = await applyUnitReconciliationMatchesRequest({
          bpId: bp.id,
          matches: selectedMatches
        })
        updatedCount = Number(
          response?.data?.meta?.updatedCount ||
            response?.data?.meta?.updated_count ||
            selectedMatches.length
        )
        notifyFloorPrefill(response?.data?.meta)
        dispatch(fetchBpRoutine({ id: bp.id }))
      } catch (error) {
        toast.error(
          getApiErrors(error) || 'Could not update the selected unit matches'
        )
        throw error
      }
    }

    if (selectedCreateUprns.length) {
      const syncResult = await new Promise(resolve => {
        dispatch(
          syncUnitsRoutine({
            bpId: bp.id,
            selectedUprns: selectedCreateUprns,
            expandNearby:
              previewContext?.nearbyExpanded !== undefined
                ? Boolean(previewContext.nearbyExpanded)
                : previewConfirmContextRef.current.expandNearby,
            nearbyRadiusIndex: Number.isInteger(
              previewContext?.nearbyRadiusIndex
            )
              ? previewContext.nearbyRadiusIndex
              : previewConfirmContextRef.current.nearbyRadiusIndex,
            isWizard: false,
            callback: resolve
          })
        )
      })

      if (!syncResult?.ok) {
        toast.error(
          'Could not create units from the public data — no saved units were deleted.'
        )
        throw new Error('Could not create units from the public data')
      }
    }

    let deletedCount = 0

    try {
      const { data } = await removeBpUnitsWithoutUprnRequest({ bpId: bp.id })
      deletedCount = Number(
        data?.data?.deleted_count ?? data?.data?.deletedCount ?? 0
      )
    } catch (error) {
      toast.error(
        getApiErrors(error) ||
          'The official units were created, but the saved units without a property reference could not be deleted.'
      )
      throw error
    }

    const summaryParts = []
    if (updatedCount > 0) {
      summaryParts.push(
        `${updatedCount} ${updatedCount === 1 ? 'unit matched' : 'units matched'}`
      )
    }
    if (deletedCount > 0) {
      summaryParts.push(
        `${deletedCount} ${deletedCount === 1 ? 'unit' : 'units'} without a property reference deleted`
      )
    }
    if (summaryParts.length) {
      toast.success(summaryParts.join(', '))
    }

    setRevertPoint(createdRevertPoint)
    markPromptStateAsCheckedLocally({ completed: true })
    previewCacheRef.current.clear()
    reconciliationCacheRef.current.clear()
    setReconciliationData(null)
    setLastReconciliationData(null)
    setReconciliationOpen(false)
    setReminderResolved(true)
    dispatch(fetchBpUnitsRoutine({ bpId: bp.id }))
  }

  const handleRevertUnits = async () => {
    if (!bp?.id || reverting) {
      return
    }

    setReverting(true)
    try {
      const { data } = await revertUnitsToRevertPointRequest({ bpId: bp.id })
      const result = data?.data || {}
      const parts = []
      if (result.removed_count > 0) {
        parts.push(`${result.removed_count} removed`)
      }
      if (result.restored_count > 0) {
        parts.push(`${result.restored_count} restored`)
      }
      if (result.renamed_count > 0) {
        parts.push(`${result.renamed_count} renamed back`)
      }
      toast.success(
        parts.length
          ? `Units reverted: ${parts.join(', ')}`
          : 'Units are already in their previous state'
      )

      setRevertPoint(null)
      setRevertConfirmOpen(false)
      previewCacheRef.current.clear()
      reconciliationCacheRef.current.clear()
      setReconciliationData(null)
      setLastReconciliationData(null)
      dispatch(fetchBpUnitsRoutine({ bpId: bp.id }))
    } catch (error) {
      toast.error(getApiErrors(error) || 'Could not revert the unit changes.')
    } finally {
      setReverting(false)
    }
  }

  const handleLookupReconciliationPostcodes = async ({
    postcodes = [],
    targetUprns = []
  } = {}) => {
    const currentData = reconciliationData || lastReconciliationData
    const selectedUprns = Array.from(
      new Set(
        (currentData?.api_units || [])
          .map(unit => normalizeUprn(unit?.uprn))
          .filter(Boolean)
      )
    )

    if (!bp?.id || !selectedUprns.length || !targetUprns.length || !postcodes.length) {
      return null
    }

    setPostcodeLookupLoading(true)

    try {
      const { data } = await lookupUnitReconciliationPostcodesRequest({
        bpId: bp.id,
        selectedUprns,
        targetUprns,
        postcodes
      })
      const normalizedData = normalizeUnitReconciliationPreview(data?.data)

      if (!normalizedData) {
        throw new Error('Postcode lookup returned no reconciliation preview')
      }

      reconciliationCacheRef.current.clear()
      setReconciliationData(normalizedData)
      setLastReconciliationData(normalizedData)

      const lookup = normalizedData?.preview?.manual_postcode_lookup || {}
      const matchedCount = Number(lookup?.matched_count || 0)
      const unresolvedCount = Number(lookup?.unresolved_count || 0)

      if (matchedCount > 0) {
        toast.success(
          `${matchedCount} ${matchedCount === 1 ? 'address was' : 'addresses were'} resolved from the added postcode${postcodes.length === 1 ? '' : 's'}.`
        )
      } else {
        toast.info('No selected UPRNs were found in the added postcode results.')
      }

      return { matchedCount, unresolvedCount }
    } catch (error) {
      toast.error(
        getApiErrors(error) || 'Could not search the additional postcode information.'
      )
      throw error
    } finally {
      setPostcodeLookupLoading(false)
    }
  }

  const handleUpdateReconciliationIgnores = async ({
    action,
    uprns = []
  } = {}) => {
    const normalizedUprns = normalizeSelectedUprns(uprns)

    if (!bp?.id || !normalizedUprns.length) {
      return
    }

    try {
      const { data } = await updateUnitReconciliationIgnoresRequest({
        bpId: bp.id,
        action,
        uprns: normalizedUprns
      })
      const ignoredUprns = normalizeSelectedUprns(
        data?.data?.ignored_uprns || data?.data?.ignoredUprns || []
      )
      const ignoredLookup = new Set(ignoredUprns)
      const applyIgnoredState = snapshot => {
        if (!snapshot) return snapshot

        const apiUnits = (snapshot.api_units || []).map(unit => ({
          ...unit,
          is_ignored: ignoredLookup.has(normalizeUprn(unit?.uprn))
        }))
        const visibleIgnoredCount = apiUnits.filter(unit => unit.is_ignored).length

        return {
          ...snapshot,
          api_units: apiUnits,
          ignored_uprns: ignoredUprns,
          summary: {
            ...(snapshot.summary || {}),
            ignored_api_units_count: visibleIgnoredCount,
            actionable_api_units_count: apiUnits.length - visibleIgnoredCount
          }
        }
      }

      setReconciliationData(previous => applyIgnoredState(previous))
      setLastReconciliationData(previous => applyIgnoredState(previous))
      reconciliationCacheRef.current.clear()

      toast.success(
        action === 'restore'
          ? `${normalizedUprns.length} ${normalizedUprns.length === 1 ? 'address was' : 'addresses were'} moved back to review.`
          : `${normalizedUprns.length} ${normalizedUprns.length === 1 ? 'address was' : 'addresses were'} ignored.`
      )
    } catch (error) {
      toast.error(
        getApiErrors(error) ||
          (action === 'restore'
            ? 'Could not restore the selected addresses.'
            : 'Could not ignore the selected addresses.')
      )
      throw error
    }
  }

  if (!canSyncUnits) {
    return null
  }

  return (
    <>
      <UnitsReconciliationModal
        open={reconciliationOpen}
        onClose={handleSkipReconciliation}
        onOpenClassicSync={handleOpenClassicSyncFromReconciliation}
        onApplySelectedMatches={handleApplyReconciliationMatches}
        onConfirmReview={handleConfirmReconciliationReview}
        onLookupPostcodes={handleLookupReconciliationPostcodes}
        onUpdateIgnored={handleUpdateReconciliationIgnores}
        onUsePublicData={handleUsePublicData}
        preferPublicData={Boolean(bp?.usePublicDataUnitNames)}
        reconciliationData={reconciliationData}
        loading={loading}
        postcodeLookupLoading={postcodeLookupLoading}
      />
      <UnitsSyncModal
        open={open}
        onConfirm={handleConfirmUnits}
        onSkip={handleSkip}
        previewData={previewData}
        nearbyExpandedFallback={previewExpandedNearby}
        loading={loading}
        confirming={confirming}
      />
      {showReminderWidget && !reminderSlot.hidden && (
        <ReminderWidget
          style={reminderSlot.style}
          type='button'
          $checked={!isReminderUrgent}
          onClick={() =>
            handleOpenReviewFlow({ openOnLoad: false, openWhenResolved: true })
          }
          aria-busy={isPreviewLoadingInBackground}
        >
          <ReminderIconWrapper
            $spinning={isPreviewLoadingInBackground}
            $urgent={!isPreviewLoadingInBackground && isReminderUrgent}
          >
            <ReminderPulse
              $urgent={!isPreviewLoadingInBackground && isReminderUrgent}
              $active={!isPreviewLoadingInBackground && isReminderUrgent}
            />
            <SyncIcon fontSize='small' />
          </ReminderIconWrapper>
          <ReminderCopy>
            <ReminderTitle>
              {isPreviewLoadingInBackground
                ? 'Checking building units'
                : hasCurrentCompletedState
                  ? 'Units sync checked'
                  : hasCurrentCheckedState
                    ? shouldUseReconciliationFlow
                      ? 'Units review checked'
                      : 'Units preview checked'
                    : shouldUseReconciliationFlow
                      ? 'Review building units'
                      : 'Units sync ready'}
            </ReminderTitle>
            <ReminderText>
              {isPreviewLoadingInBackground
                ? 'We are fetching the latest unit matches for this building.'
                : hasCurrentCompletedState
                  ? 'The current building units have already been checked and synced. Re-open only if you want to review them again.'
                  : hasCurrentCheckedState
                    ? 'The current building units have already been checked. Re-open only if you want to review the comparison again.'
                    : shouldUseReconciliationFlow &&
                        reminderActionableTotalCount > 0
                      ? `${reminderActionableTotalCount} address ${reminderActionableTotalCount === 1 ? 'unit is' : 'units are'} ready to compare against ${reminderCounts.existingUnitsCount || existingReconciliationUnitsCount} existing ${reminderCounts.existingUnitsCount === 1 || existingReconciliationUnitsCount === 1 ? 'unit' : 'units'}.`
                      : shouldUseReconciliationFlow
                        ? 'Open unit review to compare OS address data with the units already saved in this Building Passport.'
                        : reminderActionableTotalCount > 0
                          ? `${reminderActionableTotalCount} ${reminderActionableTotalCount === 1 ? 'unit is' : 'units are'} ready to review and sync.`
                          : 'Open unit sync to review the units linked to this Building Passport.'}
            </ReminderText>
          </ReminderCopy>
          <ReminderDismiss
            type='button'
            aria-label='Dismiss reminder without opening review'
            title='Dismiss without opening — no API call, no cost'
            onClick={(e) => {
              e.stopPropagation()
              // Persist a "dismissed" event so the widget stays hidden until
              // the next genuine state change. No preview API call, no LLM
              // cost. The user can re-open from the Units tab on demand.
              handleSkip({ persistReminder: true })
              setReminderResolved(true)
            }}
          >
            <CloseIcon style={{ fontSize: 14 }} />
          </ReminderDismiss>
          <ReminderBadge $checked={!isReminderUrgent}>
            {isPreviewLoadingInBackground
              ? '...'
              : hasCurrentCheckedState
                ? '✓'
                : reminderActionableTotalCount > 0
                  ? reminderActionableTotalCount
                  : '!'}
          </ReminderBadge>
        </ReminderWidget>
      )}
      {showRevertWidget && !revertSlot.hidden && (
        <RevertWidget
          style={revertSlot.style}
          type='button'
          onClick={() => setRevertConfirmOpen(true)}
        >
          <RevertIconWrapper>
            <ReplayIcon fontSize='small' />
          </RevertIconWrapper>
          <ReminderCopy>
            <ReminderTitle>Revert unit changes</ReminderTitle>
            <ReminderText>
              The unit list was rebuilt from public data
              {revertPoint?.created_at
                ? ` on ${new Date(revertPoint.created_at).toLocaleString()}`
                : ''}
              . Click to restore the previous list.
            </ReminderText>
          </ReminderCopy>
          <ReminderDismiss
            type='button'
            aria-label='Hide the revert option for now'
            title='Hide for now — you can still revert after a page refresh'
            onClick={e => {
              e.stopPropagation()
              setRevertPoint(null)
            }}
          >
            <CloseIcon style={{ fontSize: 14 }} />
          </ReminderDismiss>
        </RevertWidget>
      )}
      <Dialog
        open={revertConfirmOpen}
        onClose={reverting ? undefined : () => setRevertConfirmOpen(false)}
        sx={{ zIndex: 100002 }}
      >
        <RevertDialogBody>
          <RevertDialogTitle>Revert unit changes?</RevertDialogTitle>
          <RevertDialogText>
            This restores the unit list to how it was
            {revertPoint?.created_at
              ? ` on ${new Date(revertPoint.created_at).toLocaleString()}`
              : ' before the last "Use public data"'}
            {revertPoint?.units_count !== undefined
              ? ` (${revertPoint.units_count} ${revertPoint.units_count === 1 ? 'unit' : 'units'})`
              : ''}
            . Units created since will be removed and deleted units will be
            restored with their files.
          </RevertDialogText>
          <RevertDialogActions>
            <RevertCancelButton
              type='button'
              onClick={() => setRevertConfirmOpen(false)}
              disabled={reverting}
            >
              Cancel
            </RevertCancelButton>
            <RevertConfirmButton
              type='button'
              onClick={handleRevertUnits}
              disabled={reverting}
            >
              {reverting ? (
                <>
                  <CircularProgress size={14} style={{ color: '#fff' }} />{' '}
                  Reverting…
                </>
              ) : (
                'Revert changes'
              )}
            </RevertConfirmButton>
          </RevertDialogActions>
        </RevertDialogBody>
      </Dialog>
    </>
  )
}

export default AddressUnitsSyncPrompt

const pulse = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0.85);
    opacity: 0.72;
  }
  70% {
    transform: translate(-50%, -50%) scale(1.9);
    opacity: 0;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.9);
    opacity: 0;
  }
`

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`

const ReminderDismiss = styled.button`
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  margin-right: 4px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#94a3b8'};
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
    color: ${({ theme }) => theme.colors.haiti};
    border-color: ${({ theme }) => theme.colors.lightGrey};
  }

  /* The collapsed mobile button is a single tap target — dismissing happens
     from the review flow itself. */
  @media (max-width: 768px) {
    display: none;
  }
`

const ReminderWidget = styled.button`
  position: fixed;
  right: 24px;
  bottom: 96px;
  z-index: 10001;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 300px;
  max-width: 380px;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.haiti};
  box-shadow: 0 16px 36px rgba(12, 18, 41, 0.14);
  cursor: pointer;
  overflow: hidden;
  text-align: left;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: 4px;
    background: ${({ theme, $checked }) =>
      $checked ? theme.colors.secondary : theme.colors.primary};
  }

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ theme, $checked }) =>
      $checked ? `${theme.colors.secondary}66` : `${theme.colors.primary}55`};
    box-shadow: 0 20px 42px rgba(12, 18, 41, 0.18);
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 3px
        ${({ theme, $checked }) =>
          $checked
            ? `${theme.colors.secondary}30`
            : `${theme.colors.primary}20`},
      0 20px 42px rgba(12, 18, 41, 0.18);
  }

  /*
   * On mobile the reminder collapses into one floating button, matching the
   * Guru launcher and the other floating controls: icon only, the count as a
   * corner badge, and dismiss as a small corner button. The copy stays on
   * desktop, where there is room for it.
   */
  @media (max-width: 768px) {
    ${floatingButtonStyle}
    display: inline-flex;
    position: fixed;
    left: auto;
    right: 16px;
    z-index: 900;
    min-width: 0;
    max-width: none;
    padding: 0;
    gap: 0;
    overflow: visible;

    &::before {
      display: none;
    }

    &:hover {
      transform: none;
      box-shadow: 0 6px 18px rgba(12, 18, 41, 0.18);
    }
  }
`

const ReminderPulse = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ theme, $urgent }) =>
    $urgent ? `${theme.colors.primary}55` : `${theme.colors.secondary}55`};
  animation: ${({ $active }) =>
    $active
      ? css`
          ${pulse} 1.9s ease-out infinite
        `
      : 'none'};
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  pointer-events: none;
`

const ReminderIconWrapper = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${({ theme, $urgent }) =>
    $urgent ? `${theme.colors.primary}12` : `${theme.colors.secondary}14`};
  border: 1px solid
    ${({ theme, $urgent }) =>
      $urgent ? `${theme.colors.primary}33` : `${theme.colors.secondary}2e`};
  color: ${({ theme, $urgent }) =>
    $urgent ? theme.colors.primary : theme.colors.secondary};
  box-shadow: inset 0 0 0 1px
    ${({ theme, $urgent }) =>
      $urgent ? `${theme.colors.primary}0a` : `${theme.colors.secondary}08`};
  flex-shrink: 0;
  overflow: visible;

  svg {
    position: relative;
    z-index: 1;
    ${({ $spinning }) =>
      $spinning &&
      css`
        animation: ${spin} 1.2s linear infinite;
      `}
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 50%;
    background: transparent;
    box-shadow: none;
  }
`

const ReminderCopy = styled.span`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  @media (max-width: 768px) { display: none; }
`

const ReminderTitle = styled.span`
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
`

const ReminderText = styled.span`
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const ReminderBadge = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border-radius: 999px;
  background: ${({ theme, $checked }) =>
    $checked ? theme.colors.secondary : `${theme.colors.primary}18`};
  color: ${({ theme, $checked }) =>
    $checked ? theme.colors.haiti : theme.colors.primary};
  font-size: 13px;
  font-weight: 800;
  box-shadow: ${({ theme, $checked }) =>
    $checked
      ? `0 6px 16px ${theme.colors.secondary}33`
      : `0 6px 16px ${theme.colors.primary}22`};
  flex-shrink: 0;

  @media (max-width: 768px) {
    position: absolute;
    top: -5px;
    right: -5px;
    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    font-size: 11px;
    background: ${({ theme, $checked }) =>
      $checked ? theme.colors.secondary : theme.colors.primary};
    color: #fff;
    border: 2px solid #fff;
  }
`

const RevertWidget = styled(ReminderWidget)`
  &::before {
    background: #d97706;
  }
`

const RevertIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: #fef3c7;
  color: #b45309;
  flex-shrink: 0;
`

const RevertDialogBody = styled.div`
  padding: 22px 24px 18px;
  max-width: 420px;
`

const RevertDialogTitle = styled.h3`
  margin: 0 0 8px;
  color: #1a1a2e;
  font-size: 18px;
`

const RevertDialogText = styled.p`
  margin: 0 0 20px;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.5;
`

const RevertDialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const RevertCancelButton = styled.button`
  background: #fff;
  border: 1px solid #d1d5db;
  color: #6b7280;
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f9fafb;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const RevertConfirmButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #b00020;
  border: none;
  color: #fff;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #96001b;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
